import React from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { Calendar, Clock, User, ChevronRight, ArrowRight, Phone, Tag, BookOpen } from 'lucide-react';
import { Button } from '../components/ui/button';
import { companyInfo } from '../data/mock';
import { getBlogBySlug, getRelatedBlogs } from '../data/blogData';
import SEOHead from '../components/SEOHead';
import BlogSchema from '../components/BlogSchema';
import BlogSections from '../components/BlogSections';
import { BlogCityLinks } from '../components/InternalLinks';

const BlogPage = () => {
  const { slug } = useParams();
  const post = getBlogBySlug(slug);

  if (!post) {
    return <Navigate to="/blog" replace />;
  }

  const relatedPosts = getRelatedBlogs(post.relatedPosts || []);
  const title = post.title.split('|')[0].trim();
  const sections = post.content.sections;
  const intro = post.content.intro;
  const conclusion = post.content.conclusion;
  const cta = post.content.cta;
  const links = post.internalLinks;
  const guideLinks = post.guideLinks || [];

  return (
    <div className="bg-white" data-testid="blog-page">
      <SEOHead 
        title={post.title}
        description={post.metaDescription}
        keywords={post.tags.join(', ')}
        canonical={`/blog/${post.slug}`}
      />
      <BlogSchema post={post} />

      <BlogHero post={post} title={title} />

      <article className="max-w-4xl mx-auto px-4 py-12" data-testid="blog-content">
        <div className="bg-amber-50 border-l-4 border-amber-500 p-4 mb-8 rounded-r-lg">
          <p className="text-sm text-neutral-700"><strong>Written by {post.author}</strong> — {post.authorCredentials}</p>
        </div>
        <div className="blog-intro prose prose-lg max-w-none mb-10">
          <p className="text-xl text-neutral-700 leading-relaxed">{intro}</p>
        </div>

        <BlogTOC sections={sections} />
        <BlogSections sections={sections} internalLinks={links} />

        <section className="mb-10">
          <h2 className="text-2xl font-bold text-neutral-900 mb-4">Conclusion</h2>
          <p className="text-neutral-700 leading-relaxed">{conclusion}</p>
        </section>

        {guideLinks.length > 0 && <BlogGuideLinks guideLinks={guideLinks} />}
        
        <BlogCTABox cta={cta} phone={companyInfo.phone} />
        <BlogTags tags={post.tags} />
        <BlogServiceLink category={post.category} serviceId={post.serviceId} />
        <BlogCityLinks serviceId={post.serviceId} category={post.category} />
      </article>

      {relatedPosts.length > 0 && <RelatedPosts posts={relatedPosts} />}
      <BlogFooterCTA />
    </div>
  );
};

const BlogHero = ({ post, title }) => (
  <section className="relative py-20 bg-neutral-900">
    <div className="absolute inset-0 bg-cover bg-center opacity-30" style={{ backgroundImage: `url('${post.heroImage}')` }} />
    <div className="absolute inset-0 bg-gradient-to-r from-neutral-900 via-neutral-900/90 to-neutral-900/70" />
    <div className="relative z-10 max-w-4xl mx-auto px-4">
      <nav className="flex items-center gap-2 text-sm text-neutral-400 mb-6" data-testid="blog-breadcrumb">
        <Link to="/" className="hover:text-amber-400">Home</Link>
        <ChevronRight className="w-4 h-4" />
        <Link to="/blog" className="hover:text-amber-400">Blog</Link>
        <ChevronRight className="w-4 h-4" />
        <span className="text-amber-400">{post.category}</span>
      </nav>
      <span className="inline-flex items-center gap-1 px-3 py-1 bg-amber-500 text-white text-xs font-semibold rounded-full mb-4">
        <Tag className="w-3 h-3" />{post.category}
      </span>
      <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-6 leading-tight" data-testid="blog-title">{title}</h1>
      <div className="flex flex-wrap items-center gap-4 text-sm text-neutral-300">
        <div className="flex items-center gap-2"><User className="w-4 h-4" /><span>{post.author}</span></div>
        <div className="flex items-center gap-2"><Calendar className="w-4 h-4" /><span>{new Date(post.publishDate).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</span></div>
        <div className="flex items-center gap-2"><Clock className="w-4 h-4" /><span>{post.readTime}</span></div>
      </div>
    </div>
  </section>
);

const BlogTOC = ({ sections }) => (
  <div className="bg-neutral-50 rounded-xl p-6 mb-10" data-testid="blog-toc">
    <h2 className="text-lg font-bold text-neutral-900 mb-4">In This Guide:</h2>
    <ul className="space-y-2">
      {sections.map((s, idx) => (
        <li key={idx}>
          <a href={`#section-${idx}`} className="text-amber-600 hover:text-amber-700 flex items-center gap-2">
            <ChevronRight className="w-4 h-4" />{s.h2}
          </a>
        </li>
      ))}
    </ul>
  </div>
);

const BlogGuideLinks = ({ guideLinks }) => (
  <div className="bg-gradient-to-br from-blue-50 to-cyan-50 border border-blue-100 rounded-xl p-6 mb-10" data-testid="blog-guide-links">
    <div className="flex items-center gap-2 mb-4">
      <BookOpen className="w-5 h-5 text-blue-600" />
      <h3 className="text-lg font-bold text-neutral-900">Related Expert Guides</h3>
    </div>
    <p className="text-sm text-neutral-600 mb-4">Dive deeper into mold remediation topics with our comprehensive guides:</p>
    <ul className="space-y-3">
      {guideLinks.map((link, idx) => (
        <li key={idx}>
          <Link 
            to={link.url} 
            className="flex items-start gap-2 text-blue-700 hover:text-blue-800 transition-colors group"
          >
            <ArrowRight className="w-4 h-4 mt-0.5 flex-shrink-0 group-hover:translate-x-1 transition-transform" />
            <span className="text-sm leading-snug">{link.text}</span>
          </Link>
        </li>
      ))}
    </ul>
    <div className="mt-4 pt-4 border-t border-blue-100">
      <Link 
        to="/guides" 
        className="inline-flex items-center gap-2 text-sm font-medium text-blue-600 hover:text-blue-700"
      >
        View All Mold Remediation Guides <ArrowRight className="w-4 h-4" />
      </Link>
    </div>
  </div>
);

const BlogCTABox = ({ cta, phone }) => (
  <div className="bg-gradient-to-r from-neutral-900 to-neutral-800 rounded-2xl p-8 text-center mb-10">
    <h2 className="text-2xl font-bold text-white mb-4">Ready to Get Started?</h2>
    <p className="text-neutral-300 mb-6">{cta}</p>
    <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
      <Button asChild size="lg" className="bg-amber-500 hover:bg-amber-600 text-white" data-testid="blog-cta-estimate">
        <Link to="/contact">Get Free Estimate <ArrowRight className="w-5 h-5 ml-2" /></Link>
      </Button>
      <Button asChild size="lg" variant="outline" className="border-white text-white hover:bg-white/10">
        <a href={`tel:${phone}`}><Phone className="w-5 h-5 mr-2" />{phone}</a>
      </Button>
    </div>
  </div>
);

const BlogTags = ({ tags }) => (
  <div className="flex flex-wrap items-center gap-2 mb-10" data-testid="blog-tags">
    <span className="text-sm font-medium text-neutral-600">Tags:</span>
    {tags.map((tag, i) => (
      <span key={i} className="px-3 py-1 bg-neutral-100 text-neutral-600 text-sm rounded-full">{tag}</span>
    ))}
  </div>
);

const BlogServiceLink = ({ category, serviceId }) => (
  <div className="bg-amber-50 rounded-xl p-6 mb-10">
    <h3 className="font-bold text-neutral-900 mb-2">Need This Service?</h3>
    <p className="text-neutral-600 mb-4">Learn more about our professional {category.toLowerCase()} services in Dallas-Fort Worth.</p>
    <Button asChild className="bg-amber-500 hover:bg-amber-600" data-testid="blog-service-link">
      <Link to={`/services/${serviceId}`}>View Our {category} Services <ArrowRight className="w-4 h-4 ml-2" /></Link>
    </Button>
  </div>
);

const RelatedPosts = ({ posts }) => (
  <section className="bg-neutral-50 py-12" data-testid="blog-related-posts">
    <div className="max-w-4xl mx-auto px-4">
      <h2 className="text-2xl font-bold text-neutral-900 mb-6">Related Articles</h2>
      <div className="grid md:grid-cols-2 gap-6">
        {posts.map((related) => (
          <Link key={related.id} to={`/blog/${related.slug}`} className="bg-white rounded-xl overflow-hidden shadow-sm hover:shadow-md transition-shadow group">
            <div className="h-40 bg-cover bg-center" style={{ backgroundImage: `url('${related.heroImage}')` }} />
            <div className="p-4">
              <span className="text-xs font-semibold text-amber-600">{related.category}</span>
              <h3 className="font-bold text-neutral-900 group-hover:text-amber-600 transition-colors mt-1">{related.title.split('|')[0].trim()}</h3>
              <p className="text-sm text-neutral-500 mt-2">{related.readTime}</p>
            </div>
          </Link>
        ))}
      </div>
    </div>
  </section>
);

const BlogFooterCTA = () => (
  <section className="bg-neutral-900 py-12">
    <div className="max-w-4xl mx-auto px-4 text-center">
      <h2 className="text-2xl font-bold text-white mb-4">Serving Dallas-Fort Worth Since 1969</h2>
      <p className="text-neutral-400 mb-6">55+ years of trusted service in Dallas, Plano, Frisco, Richardson, McKinney, and surrounding areas.</p>
      <Button asChild size="lg" className="bg-amber-500 hover:bg-amber-600">
        <Link to="/contact">Contact Us Today</Link>
      </Button>
    </div>
  </section>
);

export default BlogPage;
